/**
 * QL-375 — one line's permitted range as the screens draw it: a light track,
 * the band (min–max) in grey, a darker tick at the default, and a dot where
 * the weight lands. Fixed mode has no band: the permitted weight is the tick.
 * The dot is always black (QL-664): the figure beside the bar already turns
 * red on a breach, so the bar does not say it twice.
 */

import type { MandateEvaluatedLine, MandateTemplateMode } from "@repo/shared/types";

type Props = {
  mode: MandateTemplateMode;
  line: MandateEvaluatedLine;
  /** QL-677 — false: no mandate yet, the bar carries the dot only (no band, no tick). */
  bands?: boolean;
};

/** The bar spans 0–100 %; anything the bands or the dot exceed is clamped visually. */
const clamp = (v: number) => Math.max(0, Math.min(100, v));

export function MandateRangeBar({ mode, line, bands = true }: Props) {
  const dotLeft = clamp(line.combinedPct);
  const min = mode === "fixed" ? clamp(line.weightPct ?? 0) : clamp(line.minPct ?? 0);
  const max = mode === "fixed" ? min : clamp(line.maxPct ?? 100);
  const tick = mode === "fixed" ? min : clamp(line.defaultPct ?? min);
  return (
    <div className="relative h-2 w-full rounded-full bg-muted" aria-hidden>
      {bands && mode !== "fixed" && (
        <div
          className="absolute top-0 h-full rounded-full bg-muted-foreground/30"
          style={{ left: `${min}%`, width: `${Math.max(0, max - min)}%` }}
        />
      )}
      {bands && (
        <div
          className="absolute -top-0.5 h-3 w-0.5 bg-muted-foreground/70"
          style={{ left: `calc(${tick}% - 1px)` }}
        />
      )}
      <div
        className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-foreground"
        style={{ left: `calc(${dotLeft}% - 5px)` }}
      />
    </div>
  );
}
